import React from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { RootState } from "redux/store";
import { DashboardLayout } from "component/layout/dashboard";
import { PageProps } from "shared/page-props";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { styles } from "./style";
import { saveIntegration } from "redux/slice/integrations";
import { getIntegrationsNewestToOldest } from "redux/selector";
import { AddDialog } from "./add-dialog";
import { EditForm } from "./edit-form";
import { IntegrationType } from "type/integration-type";
import { urls } from "routes/urls";
import { exchangeNylasToken } from "api/api";
import { NylasTokenRequest } from "api/resource/nylas";
import * as env from "shared/env";

const mapStateToProps = (state: RootState) => {
	return {
		integrations: getIntegrationsNewestToOldest(state),
	};
};

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	saveIntegration,
}, dispatch);

interface Props extends
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	PageProps,
	Router.RouteComponentProps,
	WithStyles<typeof styles>
{
}

interface State {
	addDialogIsOpen: boolean;
	newFubIntegrationId: number | null;
}

class Component extends React.Component<Props, State> {
	public constructor(props: Props) {
		super(props);
		this.state = {
			addDialogIsOpen: false,
			newFubIntegrationId: null,
		};
	}

	public async componentDidMount() {
		const url = new URL(window.location.href);
		const nylasUserCode = url.searchParams.get("code");
		const nylasUserState = url.searchParams.get("state");
		const { saveIntegration } = this.props;

		if (!nylasUserCode && !nylasUserState) return;

		const nylasRequest: NylasTokenRequest = {
			client_id: process.env.REACT_APP_NYLAS_CLIENT_ID_V3 || "",
			client_secret: process.env.REACT_APP_NYLAS_CLIENT_SECRET_V3 || "",
			code: nylasUserCode || "",
			redirect_uri: `${window.location.origin}${window.location.pathname}`,
			code_verifier: "nylas",
			grant_type: "authorization_code",
		};

		try {
			const response = await exchangeNylasToken(nylasRequest);

			// Only modify URL after successful token exchange
			window.history.replaceState(null, "", urls.settingsIntegrations);

			saveIntegration({
				integration: {
					integrationType: IntegrationType.EMAIL_CALENDAR,
					data: {
						nylasV3GrantId: response.grant_id,
					}
				}
			});
		} catch(error) {
			// eslint-disable-next-line
			console.error('Nylas token exchange failed:', error);
		}
	}

	public render() {
		const { user } = this.props;
		const { addDialogIsOpen, newFubIntegrationId } = this.state;
		const integrations = this.props.integrations.filter(integration => integration.integrationType.permitted(user));
		const title = "Integrations";
				return (
			<DashboardLayout
				permitted={user && user.permissions.version >= 9}
				title={title}
				header={
					<Mui.Box display="flex" alignItems="center" justifyContent="space-between">
						<Mui.Typography variant="h1">
							<FeatherIcon>
								<Icons.Settings />
							</FeatherIcon>
							{title}
						</Mui.Typography>
						<Mui.Button
							variant="contained"
							color="primary"
							onClick={() => this.setState({ addDialogIsOpen: true })}
						>
							+ Add Integration
						</Mui.Button>
					</Mui.Box>
				}
			>
				<AddDialog
					open={addDialogIsOpen}
					onClose={() => this.setState({ addDialogIsOpen: false })}
					onFubConnect={(id: number) => this.setState({ addDialogIsOpen: false, newFubIntegrationId: id })}
					user={user}
				/>
				<Mui.Grid container direction="column" spacing={4}>
					<Mui.Grid item>
						<Mui.Typography variant="body2" color="textSecondary">
							Manage your third-party integrations to streamline your workflow and keep your tools connected.
						</Mui.Typography>
					</Mui.Grid>
					<Mui.Grid item>
						<Mui.Grid container spacing={3}>
							{integrations.map((integration) => (
								<Mui.Grid key={integration.id} item xs={12} sm={6} md={4}>
									<EditForm
										integration={integration}
										autoOpenModal={integration.id === newFubIntegrationId}
										onAutoOpenHandled={() => this.setState({ newFubIntegrationId: null })}
									/>
								</Mui.Grid>
							))}
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
			</DashboardLayout>
		);
	}

}

export const SettingsIntegrationsPage = withStyles(connect(
	mapStateToProps,
	mapDispatchToProps,
)(Component), styles);